// src/App.js
import { useEffect, useState } from 'react';
import LoginScreen from './components/auth/LoginScreen';
import RegisterScreen from './components/auth/RegisterScreen';
import Header from './components/common/Header';
import Sidebar from './components/common/Sidebar';
import Dashboard from './components/screens/Dashboard';
import Officers from './components/screens/Officers';
import { AuthProvider, useAuth } from './context/AuthContext';
// Import other screen components as needed
import Reports from './components/screens/Reports';
import ScanHistory from './components/screens/ScanHistory';
import Settings from './components/screens/Settings';

// Loading component
const LoadingScreen = () => (
  <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 flex items-center justify-center">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-primary-500/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-gray-600 dark:text-gray-400">Loading...</p>
    </div>
  </div>
);

// Main Admin Panel Component
const AdminPanel = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [showOfficerModal, setShowOfficerModal] = useState(false);

  // Handle dark mode
  useEffect(() => {
    const savedDarkMode = localStorage.getItem('darkMode');
    if (savedDarkMode) {
      setDarkMode(JSON.parse(savedDarkMode));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  const handleNavigateToTab = (tabId) => {
    setActiveTab(tabId);
  };

  const handleShowOfficerModal = () => {
    setShowOfficerModal(true);
    // If not already on officers page, navigate there
    if (activeTab !== 'officers') {
      setActiveTab('officers');
    }
  };

  // Render the appropriate screen based on active tab
  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <Dashboard onNavigateToTab={handleNavigateToTab} />;
      case 'officers':
        return <Officers showModal={showOfficerModal} onCloseModal={() => setShowOfficerModal(false)} />;
      case 'scan-history':
        return <ScanHistory />;
      case 'reports':
        return <Reports />;
      case 'settings':
        return <Settings />;
      default:
        return <Dashboard onNavigateToTab={handleNavigateToTab} />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 transition-colors duration-500">
      <div className="flex h-screen">
        {/* Sidebar */}
        <Sidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
        
        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header
            onToggleSidebar={toggleSidebar}
            darkMode={darkMode}
            onToggleDarkMode={toggleDarkMode}
            onShowOfficerModal={handleShowOfficerModal}
          />
          
          <main className="flex-1 overflow-x-hidden overflow-y-auto p-6">
            <div className="max-w-7xl mx-auto">
              {renderContent()}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

// Main App Component with Authentication
const AppContent = () => {
  const { user, loading } = useAuth();
  const [showRegister, setShowRegister] = useState(false);

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return showRegister ? (
      <RegisterScreen onBackToLogin={() => setShowRegister(false)} />
    ) : (
      <LoginScreen onShowRegister={() => setShowRegister(true)} />
    );
  }

  return <AdminPanel />;
};

// Root App Component
const App = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App;